import { useState } from "react";
import { Copy, Check, ExternalLink, Bookmark, BookmarkCheck, Zap, BadgeCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TrustBadge from "@/components/TrustBadge";
import SignInPromptModal from "@/components/SignInPromptModal";
import { usePricing, finalCashbackRate } from "@/lib/pricing";

function discountLabel(c) {
  if (c.discount_type === "percent") return `${c.discount_value}% OFF`;
  if (c.discount_type === "fixed") return `$${c.discount_value} OFF`;
  if (c.discount_type === "shipping") return `FREE SHIP`;
  if (c.discount_type === "bogo") return `BOGO`;
  return "DEAL";
}

export default function CouponCard({ coupon, saved: initialSaved, onSaveChange }) {
  const pricing = usePricing();
  const cashbackRate = finalCashbackRate(pricing);
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(!!initialSaved);
  const [claimedCode, setClaimedCode] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const { user } = useAuth();

  const isDynamicCampaign = coupon.campaign_id && coupon.campaign_mode === "dynamic" && !coupon.code;
  const isOnlineCampaign = coupon.campaign_id && coupon.campaign_type === "online";
  // Build a tracker URL that carries any incoming affiliate + UTM params so we don't lose
  // attribution when a shopper clicks straight through.
  const trackerHref = coupon.campaign_id ? (() => {
    const base = `/go/campaign/${coupon.campaign_id}`;
    const qs = new URLSearchParams(window.location.search);
    const passthrough = ["aff", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
    const kept = new URLSearchParams();
    for (const k of passthrough) if (qs.get(k)) kept.set(k, qs.get(k));
    return kept.toString() ? `${base}?${kept}` : base;
  })() : null;

  const reveal = async () => {
    try { await api.post(`/coupons/${coupon.id}/click`); } catch (_e) {}
    setRevealed(true);
  };

  const claimDynamic = async () => {
    if (!user) {
      setShowSignIn(true);
      return;
    }
    setClaiming(true);
    try {
      const affParam = new URLSearchParams(window.location.search).get("aff");
      const url = `/campaigns/${coupon.campaign_id}/claim${affParam ? `?aff=${encodeURIComponent(affParam)}` : ""}`;
      const { data } = await api.post(url);
      if (data.kind === "online_redirect") {
        toast.success("Redirecting you to the merchant…");
        const target = data.redirect_url || coupon.url;
        if (target) window.open(target, "_blank", "noopener,noreferrer");
        else toast.error("Merchant hasn't set a redirect URL yet.");
        return;
      }
      setClaimedCode(data.code);
      setRevealed(true);
      if (data.reused) toast.info("You already claimed this — here's your code.");
      else toast.success("Your unique code is ready!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim code");
    } finally {
      setClaiming(false);
    }
  };

  const copy = () => {
    const val = claimedCode || coupon.code;
    if (!val) return;
    navigator.clipboard.writeText(val);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };

  const toggleSave = async () => {
    if (!user) return toast("Sign in to save coupons");
    try {
      if (saved) { await api.delete(`/user/save-coupon/${coupon.id}`); setSaved(false); }
      else { await api.post(`/user/save-coupon/${coupon.id}`); setSaved(true); }
      if (onSaveChange) onSaveChange();
    } catch { toast.error("Could not update saved coupons"); }
  };

  const displayedCode = claimedCode || coupon.code;

  return (
    <div className="pf-card p-5 flex flex-col gap-4 fade-up relative overflow-hidden group" data-testid={`coupon-card-${coupon.id}`}>
      {coupon.flash_sale && (
        <div className="absolute top-3 right-3 pf-chip pf-chip-lav">
          <Zap className="w-3 h-3" /> Flash
        </div>
      )}

      <div className="flex items-start gap-3">
        {coupon.store_logo && (
          <img src={coupon.store_logo} alt={coupon.store_name} className="w-11 h-11 rounded-xl border border-white/80 bg-white object-contain shadow-sm" data-logo="1" />
        )}
        <div className="flex-1 min-w-0">
          <Link to={`/store/${coupon.store_slug}`} className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500 hover:text-cyan-700 flex items-center gap-1">
            {coupon.store_name}
            {coupon.store_verified && (
              <span className="inline-flex items-center gap-0.5 text-cyan-600" title="Verified brand" data-testid={`verified-badge-${coupon.id}`}>
                <BadgeCheck className="w-3.5 h-3.5" />
              </span>
            )}
          </Link>
          <Link to={`/coupon/${coupon.id}`} className="block font-display font-medium text-lg leading-snug mt-1 text-slate-900 line-clamp-1 hover:text-cyan-700 transition-colors">
            {coupon.title}
          </Link>
        </div>
        <button onClick={toggleSave} className="p-1.5 rounded-full hover:bg-white/60" aria-label="Save" data-testid={`save-coupon-${coupon.id}`}>
          {saved ? <BookmarkCheck className="w-4 h-4 text-cyan-600" /> : <Bookmark className="w-4 h-4 text-slate-400" />}
        </button>
      </div>

      <p className="text-sm text-slate-600 line-clamp-2 leading-relaxed">{coupon.description}</p>
      {pricing !== undefined && (
        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-0.5" data-testid={`cashback-chip-${coupon.id}`}>
          ⭐ Earn {cashbackRate || "0.75–1%"} cashback
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="font-display text-2xl font-semibold" style={{ color: "#4A0D18" }}>
          {discountLabel(coupon)}
        </div>
        <div className="flex items-center gap-1.5">
          <TrustBadge trust={coupon.trust} testid={`coupon-trust-${coupon.id}`} />
          {coupon.cashback_percent > 0 && (
            <span className="pf-chip pf-chip-mint" data-testid={`cashback-chip-${coupon.id}`}>
              {coupon.cashback_percent}% cashback
            </span>
          )}
          {coupon.category_name && (
            <span className="pf-chip">{coupon.category_name}</span>
          )}
        </div>
      </div>

      {displayedCode ? (
        !revealed ? (
          <button
            onClick={reveal}
            className="w-full py-2.5 rounded-full border border-dashed border-cyan-400/60 text-cyan-700 font-semibold text-sm hover:bg-cyan-50/60 hover:border-cyan-500 transition-colors"
            data-testid={`reveal-code-${coupon.id}`}
          >
            {coupon.campaign_id ? "Reveal shared code →" : "Reveal code →"}
          </button>
        ) : (
          <div className="flex items-stretch gap-0 border border-white/80 rounded-full overflow-hidden bg-white/70 backdrop-blur">
            <div className="flex-1 px-4 py-2.5 font-mono font-semibold text-slate-900 tracking-wider truncate" data-testid={`code-value-${coupon.id}`}>
              {displayedCode}
            </div>
            <button onClick={copy} className="px-4 pf-btn-primary text-sm flex items-center gap-1 rounded-none" data-testid={`copy-code-${coupon.id}`}>
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        )
      ) : isOnlineCampaign ? (
        <a
          href={trackerHref}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-2.5 rounded-full pf-btn-primary text-sm flex items-center justify-center gap-2"
          data-testid={`tracker-link-${coupon.id}`}
        >
          Get deal <ExternalLink className="w-4 h-4" />
        </a>
      ) : isDynamicCampaign ? (
        <button
          onClick={claimDynamic}
          disabled={claiming}
          className="w-full py-2.5 rounded-full pf-btn-primary text-sm flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid={`claim-campaign-${coupon.id}`}
        >
          <Sparkles className="w-4 h-4" />
          {claiming ? "Generating…" : user ? "Get my unique code →" : "Sign in to claim →"}
        </button>
      ) : (
        <button
          onClick={reveal}
          className="w-full py-2.5 rounded-full pf-btn-primary text-sm flex items-center justify-center gap-2"
          data-testid={`get-deal-${coupon.id}`}
        >
          Get deal <ExternalLink className="w-4 h-4" />
        </button>
      )}

      <SignInPromptModal
        open={showSignIn}
        onClose={() => setShowSignIn(false)}
        storeName={coupon.store_name}
      />
    </div>
  );
}
